import { Alert, AlertTitle, Grid, InputAdornment, Stack, styled, TextField, Typography } from "@mui/material";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { Children, cloneElement, FC, isValidElement, ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentUtcDate } from "../../../lib/Util";
import { useBackfillStore } from "../../../store/backfillsStore";
import DateRangePicker from "../../Date/DateRangePicker";
import { ProgressBarTitle } from "../ProgressBarTitle";
const descriptionLength = 4000;

enum ErrorType {
  empty = "",
  conflictingBackfillsError = "ConflictingBackfillsError",
  otherError = "OtherError",
}

const ErrorMessageContainer = styled("div")({
  width: "60%",
});

const IconInfoStyled = styled(InfoIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.surfaceLight30.main,
}));

const DynamicDiagramSmallContainer = styled(Stack)({
  overflowY: "auto",
  "&:hover .hide": {
    visibility: "visible",
  },

  ".hide": {
    visibility: "hidden",
  },
});

const BackfillError = () => {
  const isConflictingBackfillsError = useBackfillStore(
    (state) => state.errorType === ErrorType.conflictingBackfillsError
  );
  const conflictingBackfills = useBackfillStore((state) => state.conflictingBackfills);
  let errorContent = <div>Please go back and try again</div>;
  let errorTitle = "There was an error while processing the backfill";
  if (isConflictingBackfillsError) {
    errorContent = (
      <Stack spacing={1}>
        {conflictingBackfills.map((backfill, idx) => {
          return (
            <Link
              key={idx}
              data-testid={`conflicting-backfill-${idx}`}
              to={{
                pathname: `../details/${backfill.id}`,
                search: `?backfillName=${encodeURIComponent(backfill.title)}`,
              }}
            >
              {backfill.title}
            </Link>
          );
        })}
      </Stack>
    );

    errorTitle = "We found one or more conflicting backfill in progress. Click below to view.";
  }
  return (
    <ErrorMessageContainer>
      <Alert severity="error" className={"backfillError"} data-cy="error-alert">
        <AlertTitle>{errorTitle}</AlertTitle>
        {errorContent}
      </Alert>
    </ErrorMessageContainer>
  );
};

interface IBackfillReview {
  title: string;
  children: ReactNode;
  completedPercentage: number;
}

const NameInput = () => {
  const backfillName = useBackfillStore((state) => state.backfillName);
  const updateName = useBackfillStore((state) => state.updateName);
  return (
    <TextField
      fullWidth
      variant="outlined"
      label="Name"
      onChange={(event) => {
        updateName(event.target.value);
      }}
      value={backfillName}
      autoComplete="off"
      data-testid="backfillName"
    />
  );
};

const DescriptionInput = () => {
  const backfillDescription = useBackfillStore((state) => state.backfillDescription);

  const updateDescription = useBackfillStore((state) => state.updateDescription);

  return (
    <TextField
      fullWidth
      id="outlined-multiline-flexible"
      label="Description"
      data-cy="backfill-description"
      value={backfillDescription}
      onChange={(event) => updateDescription(event.target.value)}
      multiline
      minRows={10}
      maxRows={10}
      autoComplete="off"
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                position: "absolute",
                right: "5px",
                bottom: "10px",
              }}
            >
              / {descriptionLength - backfillDescription?.length}
            </InputAdornment>
          ),
        },

        htmlInput: {
          maxLength: 4000,
        },
      }}
    />
  );
};

const RangeDate = () => {
  const date = useBackfillStore((state) => state.date);
  const updateDate = useBackfillStore((state) => state.updateDate);

  return (
    <DateRangePicker
      disableHighlightToday
      start={date.start}
      end={date.end}
      defaultEnd={getCurrentUtcDate()}
      defaultStart={getCurrentUtcDate()}
      disableFuture
      calendars={1}
      onChange={(value) => {
        updateDate({ start: value[0], end: value[1] });
      }}
      customHelper={
        <>
          <IconInfoStyled />
          {"The dates are in UTC timezone"}
        </>
      }
      format="MMM dd, yyyy"
      sx={{ width: "100%" }}
    />
  );
};

const BackfillReview: FC<IBackfillReview> = ({ title, children, completedPercentage }) => {
  const showError = useBackfillStore((state) => state.errorType !== ErrorType.empty);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const checkOverflow = () => {
    const container = containerRef.current;
    if (container) {
      setHasOverflow(container.scrollHeight > container.clientHeight);
    }
  };

  useEffect(() => {
    checkOverflow();
  }, []);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <ProgressBarTitle title={title} completedPercentage={completedPercentage} />
      <Stack spacing={1} sx={{ width: "100%" }}>
        <Typography variant="body.medium.regular">
          Choose a name for your backfill. Optionally, add a description.
        </Typography>
        {showError && <BackfillError />}
      </Stack>
      <Grid container sx={{ justifyContent: "center", height: "90%", alignItems: "center", display: "flex" }}>
        <Grid container sx={{ flexDirection: "column" }} size={4} spacing={2}>
          <Grid data-testid="date-picker-item" sx={{ mb: 2 }}>
            <RangeDate />
          </Grid>
          <Grid>
            <NameInput />
          </Grid>
          <Grid>
            <DescriptionInput />
          </Grid>
        </Grid>
        <Grid container sx={{ flexDirection: "column" }} size={{ xs: 12, md: 6 }}>
          <Stack sx={{ maxHeight: "45vh" }}>
            <DynamicDiagramSmallContainer ref={containerRef}>
              {Children.map(children, (child) =>
                isValidElement<{ showIcons: boolean }>(child)
                  ? cloneElement(child, { showIcons: Boolean(!hasOverflow) })
                  : child
              )}
            </DynamicDiagramSmallContainer>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export { BackfillReview };
